// Geração de relatórios (HTML autossuficiente) para download/e-mail. Usado por
// "Saída de linha" e "Rampa de lançamentos". Gera gráficos em SVG inline (sem
// dependências) e baixa como arquivo .html que abre em qualquer navegador e
// pode ser impresso em PDF.

export interface ReportSeriesPoint {
  label: string; // rótulo do mês (ex.: "mai/26")
  value: number | null;
}
export interface ReportIndicator {
  title: string;
  meta: number | null;
  points: ReportSeriesPoint[];
  fmt: (v: number | null) => string;
  lowerIsBetter?: boolean;
}
export interface ReportHeaderItem {
  label: string;
  value: string;
}
export interface ReportSpec {
  title: string; // nome do produto
  brand?: string | null; // marca (agrupa o relatório geral e define a cor do cabeçalho)
  subtitle?: string; // marca · código
  tagline?: string; // ex.: status / "Rampa de lançamento"
  photoUrl?: string | null;
  header: ReportHeaderItem[]; // pares rótulo/valor do cabeçalho
  indicators: ReportIndicator[];
  // Tabela de lançamentos mensais (cabeçalho + linhas já formatadas)
  tableHead?: string[];
  tableRows?: string[][];
  // Blocos de texto (histórico, comentários)
  sections?: { title: string; items: { head: string; body: string }[] }[];
  footerNote?: string;
  // Rótulos fixos da UI do relatório (PT por padrão; passe EN para a gestão alemã).
  labels?: ReportLabels;
}

export interface ReportLabels {
  indicators: string;
  monthlyTable: string;
  last: string;
  meta: string;
  lowerBetter: string;
  noPhoto: string;
  brand: string;
  noBrand: string;
  indexByBrand: string;
  itemOne: string;
  itemMany: string;
  brandOne: string;
  brandMany: string;
  insights: string;
  attention: string;
  highlights: string;
  goalsHit: string;
  ofWord: string;
  indicatorsWord: string;
  nothing: string;
  worse3: string;
  better3: string;
  negative: string;
}
const PT_LABELS: ReportLabels = {
  indicators: 'Indicadores',
  monthlyTable: 'Lançamentos mensais',
  last: 'Último',
  meta: 'meta',
  lowerBetter: ' (menor é melhor)',
  noPhoto: 'sem foto',
  brand: 'Marca',
  noBrand: 'Sem marca',
  indexByBrand: 'Índice por marca',
  itemOne: 'item',
  itemMany: 'itens',
  brandOne: 'marca',
  brandMany: 'marcas',
  insights: 'Insights',
  attention: 'Pontos de atenção',
  highlights: 'Destaques',
  goalsHit: 'Metas atingidas (último mês lançado)',
  ofWord: 'de',
  indicatorsWord: 'indicadores com meta',
  nothing: 'Nada a destacar',
  worse3: 'piorando há 3 meses',
  better3: 'melhorando há 3 meses',
  negative: 'valor negativo',
};
export const EN_LABELS: ReportLabels = {
  indicators: 'Indicators',
  monthlyTable: 'Monthly figures',
  last: 'Latest',
  meta: 'target',
  lowerBetter: ' (lower is better)',
  noPhoto: 'no photo',
  brand: 'Brand',
  noBrand: 'No brand',
  indexByBrand: 'Index by brand',
  itemOne: 'item',
  itemMany: 'items',
  brandOne: 'brand',
  brandMany: 'brands',
  insights: 'Insights',
  attention: 'Watch-outs',
  highlights: 'Highlights',
  goalsHit: 'Targets met (latest month reported)',
  ofWord: 'of',
  indicatorsWord: 'indicators with a target',
  nothing: 'Nothing to highlight',
  worse3: 'worsening for 3 months',
  better3: 'improving for 3 months',
  negative: 'negative value',
};

// Identidade visual por marca (cores do cabeçalho do relatório). Marcas não reconhecidas usam o azul padrão.
interface BrandTheme {
  c1: string;
  c2: string;
}
const BRAND_THEMES: { match: RegExp; theme: BrandTheme }[] = [
  { match: /trevo\s*kids/i, theme: { c1: '#d9730d', c2: '#f4a62a' } },
  { match: /ehrmann|\bhp\b/i, theme: { c1: '#0b4f9c', c2: '#2b7fd4' } },
  { match: /apreciare/i, theme: { c1: '#6b2c91', c2: '#a24fc4' } },
  { match: /pulsi/i, theme: { c1: '#d6401f', c2: '#f0702c' } },
  { match: /high\s*protein/i, theme: { c1: '#0f6b5f', c2: '#1fa391' } },
  { match: /\babc\b/i, theme: { c1: '#a61b1b', c2: '#e04545' } },
  { match: /trevo/i, theme: { c1: '#17703a', c2: '#34a853' } },
];
const DEFAULT_THEME: BrandTheme = { c1: '#13294b', c2: '#1f4e79' };
function brandTheme(name?: string | null): BrandTheme {
  const n = (name ?? '').trim();
  return BRAND_THEMES.find((b) => b.match.test(n))?.theme ?? DEFAULT_THEME;
}
const themeStyle = (name?: string | null) => {
  const t = brandTheme(name);
  return `--c1:${t.c1};--c2:${t.c2}`;
};

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** altura do gráfico: mais baixo quando o item tem muitos indicadores (para caber numa folha A4). */
function chartSvg(ind: ReportIndicator, h = 190): string {
  const w = 360, padL = 10, padR = 10, padT = 24, padB = 40;
  const vals = ind.points.map((p) => p.value).filter((v): v is number => v != null);
  const all = [...vals, 0, ...(ind.meta != null ? [ind.meta] : [])];
  const maxV = Math.max(1, ...all);
  const minV = Math.min(0, ...all);
  const span = maxV - minV || 1;
  const n = Math.max(1, ind.points.length);
  const x = (i: number) => padL + (i * (w - padL - padR)) / Math.max(1, n - 1);
  const y = (v: number) => padT + (1 - (v - minV) / span) * (h - padT - padB);
  const hasNeg = minV < 0;

  const pts = ind.points
    .map((p, i) => (p.value == null ? null : `${x(i)},${y(p.value)}`))
    .filter((s): s is string => s != null)
    .join(' ');

  let svg = `<svg viewBox="0 0 ${w} ${h}" width="100%" preserveAspectRatio="xMidYMid meet" style="display:block">`;
  if (hasNeg) {
    svg += `<line x1="${padL}" x2="${w - padR}" y1="${y(0)}" y2="${y(0)}" stroke="#9aa3ad" stroke-width="1"/>`;
    svg += `<text x="${padL}" y="${y(0) - 2}" font-size="9" fill="#9aa3ad">0</text>`;
  }
  if (ind.meta != null) {
    svg += `<line x1="${padL}" x2="${w - padR}" y1="${y(ind.meta)}" y2="${y(ind.meta)}" stroke="#2e7d32" stroke-width="1.5" stroke-dasharray="4 3"/>`;
    svg += `<text x="${w - padR}" y="${y(ind.meta) - 3}" text-anchor="end" font-size="10" fill="#2e7d32">meta ${esc(ind.fmt(ind.meta))}</text>`;
  }
  if (pts) svg += `<polyline points="${pts}" fill="none" stroke="#1d4ed8" stroke-width="2"/>`;
  ind.points.forEach((p, i) => {
    if (p.value == null) return;
    const neg = p.value < 0;
    const ly = neg ? y(p.value) + 12 : y(p.value) - 6;
    svg += `<circle cx="${x(i)}" cy="${y(p.value)}" r="3" fill="${neg ? '#c0392b' : '#1d4ed8'}"/>`;
    svg += `<text x="${x(i)}" y="${ly}" text-anchor="middle" font-size="9" font-weight="600" fill="${neg ? '#c0392b' : '#222'}">${esc(ind.fmt(p.value))}</text>`;
  });
  // Rótulos de mês inclinados (−40°) para não sobrepor quando há muitos meses.
  ind.points.forEach((p, i) => {
    const lx = x(i), ly = h - padB + 12;
    svg += `<text x="${lx}" y="${ly}" text-anchor="end" font-size="8.5" fill="#888" transform="rotate(-40 ${lx} ${ly})">${esc(p.label)}</text>`;
  });
  svg += `</svg>`;
  return svg;
}

// ---------------------------------------------------------------------------
// Insights (bandeiras vermelhas / verdes) — resumo curto calculado a partir dos próprios indicadores.
// ---------------------------------------------------------------------------
interface Flag {
  text: string;
  score: number;
}
interface FlagsResult {
  bad: Flag[];
  good: Flag[];
  metaTotal: number;
  metaHit: number;
}

function calcularFlags(specs: ReportSpec[], L: ReportLabels, comNome: boolean): FlagsResult {
  const bad: Flag[] = [];
  const good: Flag[] = [];
  let metaTotal = 0;
  let metaHit = 0;
  for (const spec of specs) {
    for (const ind of spec.indicators) {
      const vals = ind.points.filter((p): p is { label: string; value: number } => p.value != null);
      if (!vals.length) continue;
      const last = vals[vals.length - 1];
      if (!last) continue;
      const lower = !!ind.lowerIsBetter;
      let trend = 0;
      if (vals.length >= 3) {
        const [a = 0, b = 0, c = 0] = vals.slice(-3).map((v) => v.value);
        trend = a < b && b < c ? 1 : a > b && b > c ? -1 : 0;
      }
      const worsening = lower ? trend === 1 : trend === -1;
      const improving = lower ? trend === -1 : trend === 1;
      let metaOk: boolean | null = null;
      let gap = 0;
      if (ind.meta != null) {
        metaTotal++;
        metaOk = lower ? last.value <= ind.meta : last.value >= ind.meta;
        if (metaOk) metaHit++;
        gap = Math.min(3, Math.abs(last.value - ind.meta) / Math.max(Math.abs(ind.meta), 1));
      }
      const negative = !lower && last.value < 0;
      const notas: string[] = [];
      if (negative) notas.push(L.negative);
      if (worsening) notas.push(L.worse3);
      else if (improving) notas.push(L.better3);
      const text =
        (comNome ? `${spec.title} — ` : '') +
        `${ind.title}: ${ind.fmt(last.value)}` +
        (ind.meta != null ? ` (${L.meta} ${ind.fmt(ind.meta)})` : '') +
        ` · ${last.label}` +
        (notas.length ? ` · ${notas.join(', ')}` : '');
      if (negative || metaOk === false || worsening) {
        bad.push({ text, score: (negative ? 2 : 0) + (metaOk === false ? gap : 0) + (worsening ? 0.5 : 0) });
      } else if (metaOk === true || improving) {
        good.push({ text, score: (metaOk === true ? gap : 0) + (improving ? 0.5 : 0) });
      }
    }
  }
  bad.sort((x, y) => y.score - x.score);
  good.sort((x, y) => y.score - x.score);
  return { bad, good, metaTotal, metaHit };
}

/**
 * Bloco de insights. Mostra TODOS os comentários; na impressão, se a capa passar de uma folha, o script
 * (FIT_SCRIPT) esconde os MENOS importantes (a lista já vem ordenada do mais para o menos importante).
 * Não existe "+N outros": quem lê o PDF não tem acesso ao painel para ver o que ficou de fora.
 * `aparar` liga esse corte automático (só na capa do relatório geral).
 */
function insightsHtml(specs: ReportSpec[], L: ReportLabels, comNome: boolean, aparar: boolean): string {
  const f = calcularFlags(specs, L, comNome);
  if (!f.bad.length && !f.good.length && !f.metaTotal) return '';
  const lista = (flags: Flag[]) =>
    flags.length
      ? `<ul>${flags.map((x) => `<li>${esc(x.text)}</li>`).join('')}</ul>`
      : `<div class="none">${esc(L.nothing)}</div>`;
  const resumo = f.metaTotal
    ? `<div class="ins-sum">${esc(L.goalsHit)}: <b>${f.metaHit} ${esc(L.ofWord)} ${f.metaTotal}</b> ${esc(L.indicatorsWord)}</div>`
    : '';
  return `<div class="ins"${aparar ? ' data-trim="1"' : ''}><h2>${esc(L.insights)}</h2>${resumo}<div class="ins-grid">
    <div class="ins-box bad"><h3>${esc(L.attention)}</h3>${lista(f.bad)}</div>
    <div class="ins-box good"><h3>${esc(L.highlights)}</h3>${lista(f.good)}</div>
  </div></div>`;
}

// ---------------------------------------------------------------------------
// Fotos leves: reduz cada foto (ex.: PNG de vários MB) para ~480 px em JPEG antes de montar o relatório.
// A foto aparece com ~26 mm no papel, então 480 px mantém a nitidez e o PDF fica bem menor.
// ---------------------------------------------------------------------------
const FOTO_MAX_PX = 480;
const cacheFotos = new Map<string, Promise<string>>();

function fotoLeve(url: string): Promise<string> {
  let p = cacheFotos.get(url);
  if (!p) {
    p = (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) return url;
        const bmp = await createImageBitmap(await resp.blob());
        const fator = Math.min(1, FOTO_MAX_PX / Math.max(bmp.width, bmp.height));
        const w = Math.max(1, Math.round(bmp.width * fator));
        const h = Math.max(1, Math.round(bmp.height * fator));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return url;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bmp, 0, 0, w, h);
        bmp.close();
        return canvas.toDataURL('image/jpeg', 0.9);
      } catch {
        return url; // sem rede/CORS: mantém a foto original (o relatório continua funcionando)
      }
    })();
    cacheFotos.set(url, p);
  }
  return p;
}

/** Foto única reduzida (ou null se não houver). */
export async function prepararFoto(url: string | null | undefined): Promise<string | null> {
  return url ? fotoLeve(url) : null;
}

/** Devolve cópias dos itens com a foto já reduzida (use antes de montar o relatório). */
export async function prepararFotos<T extends { photoUrl?: string | null }>(specs: T[]): Promise<T[]> {
  return Promise.all(specs.map(async (s) => (s.photoUrl ? { ...s, photoUrl: await fotoLeve(s.photoUrl) } : s)));
}

const REPORT_CSS = `
  :root { --green:#2e7d32; --navy:#13294b; --ink:#1f2937; --muted:#6b7280; --line:#e5e7eb; }
  /* Força as CORES (fundos, degradês e imagens) também na impressão / PDF. Sem isto os navegadores
     descartam os fundos e o texto branco do cabeçalho some. */
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: var(--ink); margin: 0; background:#f3f4f6; }
  .page { max-width: 900px; margin: 0 auto 18px; background:#fff; box-shadow:0 1px 4px #0001; }
  .hero { background: linear-gradient(135deg, var(--c1, var(--navy)), var(--c2, #1f4e79)); color:#fff; padding: 22px 28px; display:flex; gap:20px; align-items:center; }
  /* A foto do produto aparece INTEIRA e COLORIDA, numa moldura branca (sem cortar nem estourar). */
  .photo { width: 100px; height: 100px; border-radius: 14px; object-fit: contain; background:#fff; padding:6px; box-shadow:0 1px 5px #0004; flex:0 0 100px; }
  .photo.ph { display:flex; align-items:center; justify-content:center; font-size:11px; color:#6b7280; text-align:center; }
  .brandbar { background: linear-gradient(90deg, var(--c1, var(--navy)), var(--c2, #1f4e79)); color:#fff; padding: 9px 28px; display:flex; justify-content:space-between; align-items:center; font-weight:800; font-size:13px; letter-spacing:.08em; text-transform:uppercase; }
  .brandbar span { font-weight:600; letter-spacing:0; text-transform:none; opacity:.92; }
  .idx-brand { display:flex; gap:10px; align-items:flex-start; padding:8px 0; border-bottom:1px solid var(--line); font-size:13px; }
  .idx-dot { width:14px; height:14px; border-radius:50%; background: linear-gradient(135deg, var(--c1), var(--c2)); flex:0 0 14px; margin-top:2px; }
  .idx-brand b { display:block; font-size:14px; }
  .idx-brand small { color:var(--muted); font-size:12px; }
  .hero h1 { margin:0 0 4px; font-size: 21px; }
  .hero .sub { opacity:.9; font-size: 13px; }
  .hero .tag { display:inline-block; margin-top:8px; background:#ffffff26; border:1px solid #ffffff44; padding:2px 10px; border-radius:999px; font-size:12px; }
  .body { padding: 20px 28px 30px; }
  .kvs { display:grid; grid-template-columns: repeat(auto-fit,minmax(150px,1fr)); gap:10px 18px; margin-bottom: 14px; }
  .kv { display:flex; flex-direction:column; font-size:13px; }
  .kv span { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:.04em; }
  h2 { font-size: 15px; border-bottom:2px solid var(--c1, var(--green)); padding-bottom:4px; margin: 20px 0 12px; }
  .charts { display:grid; grid-template-columns: repeat(2,1fr); gap: 16px; }
  .chart { border:1px solid var(--line); border-radius:10px; padding:10px 12px; }
  .chart-title { font-size:12.5px; font-weight:700; color:#374151; margin-bottom:4px; }
  .chart-foot { font-size:12px; color:#4b5563; margin-top:4px; }
  .tbl { width:100%; border-collapse: collapse; font-size:12px; }
  .tbl th, .tbl td { border:1px solid var(--line); padding:5px 7px; text-align:right; }
  .tbl th:first-child, .tbl td:first-child { text-align:left; }
  .tbl thead th { background:#f8fafc; }
  .sec-item { border-left:3px solid var(--line); padding:3px 0 3px 10px; margin-bottom:8px; }
  .sec-head { font-size:12px; color:var(--muted); }
  .sec-body { font-size:13px; white-space:pre-wrap; }
  .foot { padding: 12px 28px; color:var(--muted); font-size:11px; border-top:1px solid var(--line); }
  .cover { padding: 26px 28px; }
  .ins h2 { margin-top: 4px; }
  .ins-sum { font-size:12.5px; color:#374151; margin:-4px 0 10px; }
  .ins-grid { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
  .ins-box { border-radius:10px; padding:9px 12px; font-size:12px; }
  .ins-box h3 { margin:0 0 5px; font-size:12.5px; }
  .ins-box ul { margin:0; padding:0; list-style:none; }
  .ins-box li { position:relative; padding:2px 0 2px 14px; line-height:1.35; }
  .ins-box li::before { content:''; position:absolute; left:0; top:7px; width:8px; height:8px; border-radius:50%; }
  .ins-box .none { color:var(--muted); }
  .ins-box.bad { background:#fef2f2; border:1px solid #fecaca; } .ins-box.bad h3 { color:#b91c1c; } .ins-box.bad li::before { background:#dc2626; }
  .ins-box.good { background:#f0fdf4; border:1px solid #bbf7d0; } .ins-box.good h3 { color:#15803d; } .ins-box.good li::before { background:#16a34a; }
  .cover h1 { margin:0; font-size:22px; color:var(--navy); }
  /* IMPRESSÃO / PDF: cada item ocupa EXATAMENTE uma folha A4. O conteúdo é reduzido (script abaixo) se for
     maior que a folha; a página tem altura fixa e nunca "vaza" para a seguinte. */
  @page { size: A4; margin: 8mm; }
  @media print {
    body { background:#fff; }
    .page { max-width:none; width:194mm; height:279mm; margin:0; overflow:hidden; box-shadow:none; break-after: page; page-break-after: always; break-inside: avoid; }
    .page:last-child { break-after:auto; page-break-after:auto; }
    .fit { transform-origin: top left; }
    .photo { box-shadow: none; }
  }
  @media (max-width:640px){ .charts, .ins-grid { grid-template-columns:1fr; } }
`;

/** Corpo (indicadores + tabela + seções) de um item — reutilizado em relatórios único e geral. */
function renderItemPage(spec: ReportSpec, L: ReportLabels, brandHeader?: string, withInsights = false): string {
  const headerRows = spec.header
    .map((h) => `<div class="kv"><span>${esc(h.label)}</span><b>${esc(h.value)}</b></div>`)
    .join('');
  const chartH = spec.indicators.length > 7 ? 135 : spec.indicators.length > 6 ? 150 : 190;
  const charts = spec.indicators
    .map((ind) => {
      const last = [...ind.points].reverse().find((p) => p.value != null)?.value ?? null;
      return `<div class="chart">
        <div class="chart-title">${esc(ind.title)}</div>
        ${chartSvg(ind, chartH)}
        <div class="chart-foot">${esc(L.last)}: <b>${esc(ind.fmt(last))}</b>${
          ind.meta != null ? ` · ${esc(L.meta)} <b>${esc(ind.fmt(ind.meta))}</b>${ind.lowerIsBetter ? esc(L.lowerBetter) : ''}` : ''
        }</div>
      </div>`;
    })
    .join('');
  let table = '';
  if (spec.tableHead && spec.tableRows && spec.tableRows.length) {
    table = `<h2>${esc(L.monthlyTable)}</h2>
    <table class="tbl"><thead><tr>${spec.tableHead.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${spec.tableRows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  }
  const sections = (spec.sections ?? [])
    .filter((s) => s.items.length)
    .map((s) => `<h2>${esc(s.title)}</h2>${s.items
      .map((it) => `<div class="sec-item"><div class="sec-head">${esc(it.head)}</div><div class="sec-body">${esc(it.body)}</div></div>`)
      .join('')}`)
    .join('');
  const photo = spec.photoUrl
    ? `<img class="photo" src="${esc(spec.photoUrl)}" alt=""/>`
    : `<div class="photo ph">${esc(L.noPhoto)}</div>`;
  return `<div class="page" style="${themeStyle(spec.brand)}"><div class="fit">
  ${brandHeader ?? ''}
  <div class="hero">
    ${photo}
    <div>
      <h1>${esc(spec.title)}</h1>
      ${spec.subtitle ? `<div class="sub">${esc(spec.subtitle)}</div>` : ''}
      ${spec.tagline ? `<span class="tag">${esc(spec.tagline)}</span>` : ''}
    </div>
  </div>
  <div class="body">
    <div class="kvs">${headerRows}</div>
    ${withInsights ? insightsHtml([spec], L, false, false) : ''}
    ${spec.indicators.length ? `<h2>${esc(L.indicators)}</h2><div class="charts">${charts}</div>` : ''}
    ${table}
    ${sections}
  </div>
  ${spec.footerNote ? `<div class="foot">${esc(spec.footerNote)}</div>` : ''}
</div></div>`;
}

/** Na impressão, reduz o conteúdo de cada página que passar de uma folha A4 (194 x 279 mm úteis). */
const FIT_SCRIPT = `(function(){
  var W = 194 / 25.4 * 96, H = 279 / 25.4 * 96 - 4;
  function fit(){
    reset();
    document.querySelectorAll('.page').forEach(function(p){
      var f = p.querySelector('.fit'); if (!f) return;
      f.style.width = W + 'px'; f.style.transform = '';
      var ins = f.querySelector('.ins[data-trim]');
      if (ins) {
        // Capa: se não couber numa folha, esconde os comentários menos importantes (os últimos de cada lista,
        // sempre da lista mais longa). 1) até sobrar 6 em cada; 2) se ainda não couber, some a lista de nomes
        // do índice por marca (fica só marca + quantidade), devolve os comentários e corta de novo (mínimo 2).
        var uls = ins.querySelectorAll('.ins-box ul');
        var cortar = function(minimo){
          while (f.offsetHeight > H) {
            var alvo = null, max = minimo;
            uls.forEach(function(ul){
              var v = ul.querySelectorAll('li:not([data-off])');
              if (v.length > minimo && v.length >= max) { max = v.length; alvo = v[v.length - 1]; }
            });
            if (!alvo) break;
            alvo.setAttribute('data-off', '1'); alvo.style.display = 'none';
          }
        };
        cortar(6);
        if (f.offsetHeight > H) {
          ins.querySelectorAll('li[data-off]').forEach(function(li){ li.removeAttribute('data-off'); li.style.display = ''; });
          f.querySelectorAll('.idx-brand small').forEach(function(sm){ sm.setAttribute('data-off', '1'); sm.style.display = 'none'; });
          cortar(2);
        }
      }
      var h = f.offsetHeight;
      if (h > H) { var z = H / h; f.style.width = (W / z) + 'px'; f.style.transform = 'scale(' + z + ')'; }
    });
  }
  function reset(){
    document.querySelectorAll('.fit').forEach(function(f){ f.style.width = ''; f.style.transform = ''; });
    document.querySelectorAll('[data-off]').forEach(function(el){ el.removeAttribute('data-off'); el.style.display = ''; });
  }
  window.addEventListener('beforeprint', fit);
  window.addEventListener('afterprint', reset);
  if (window.matchMedia) { var mq = window.matchMedia('print'); if (mq.addListener) mq.addListener(function(e){ if (e.matches) fit(); else reset(); }); }
})();`;

function wrapDoc(title: string, inner: string, autoPrint: boolean): string {
  const printScript = autoPrint
    ? `<script>window.addEventListener('load',function(){setTimeout(function(){window.focus();window.print();},500);});</script>`
    : '';
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${esc(title)}</title><style>${REPORT_CSS}</style></head>
<body>${inner}<script>${FIT_SCRIPT}</script>${printScript}</body></html>`;
}

/** Relatório de um item. autoPrint abre a caixa de impressão (salvar em PDF). */
export function buildReportHtml(spec: ReportSpec, autoPrint = false): string {
  const L = spec.labels ?? PT_LABELS;
  return wrapDoc(spec.title, renderItemPage(spec, L, undefined, true), autoPrint);
}

/** Agrupa os itens por marca (marcas em ordem alfabética, sem marca por último; itens por nome). */
function agruparPorMarca(items: ReportSpec[]): { brand: string; items: ReportSpec[] }[] {
  const grupos = new Map<string, ReportSpec[]>();
  for (const s of items) {
    const k = (s.brand ?? '').trim();
    const lista = grupos.get(k);
    if (lista) lista.push(s);
    else grupos.set(k, [s]);
  }
  const nomes = [...grupos.keys()].sort((a, b) => {
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, 'pt-BR');
  });
  return nomes.map((n) => ({
    brand: n,
    items: [...(grupos.get(n) ?? [])].sort((x, y) => x.title.localeCompare(y.title, 'pt-BR')),
  }));
}

/**
 * Relatório GERAL: capa colorida com índice por marca + uma página por item, AGRUPADOS POR MARCA
 * (cada marca começa com uma faixa na cor da marca; cada item vira uma página no PDF).
 */
export function buildListReportHtml(
  opts: { title: string; subtitle?: string; items: ReportSpec[]; labels?: ReportLabels; footerNote?: string },
  autoPrint = false,
): string {
  const L = opts.labels ?? PT_LABELS;
  const grupos = agruparPorMarca(opts.items);
  const nomeMarca = (b: string) => b || L.noBrand;
  const palavra = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

  const indice = grupos
    .map(
      (g) => `<div class="idx-brand" style="${themeStyle(g.brand)}"><span class="idx-dot"></span><div>
        <b>${esc(nomeMarca(g.brand))} · ${palavra(g.items.length, L.itemOne, L.itemMany)}</b>
        <small>${g.items.map((s) => esc(s.title)).join(' · ')}</small>
      </div></div>`,
    )
    .join('');
  const cover = `<div class="page"><div class="fit"><div class="hero"><div>
    <h1>${esc(opts.title)}</h1>
    ${opts.subtitle ? `<div class="sub">${esc(opts.subtitle)}</div>` : ''}
    <span class="tag">${palavra(opts.items.length, L.itemOne, L.itemMany)} · ${palavra(grupos.length, L.brandOne, L.brandMany)}</span>
  </div></div>
  <div class="body">${insightsHtml(opts.items, L, true, true)}<h2>${esc(L.indexByBrand)}</h2>${indice}</div>
  ${opts.footerNote ? `<div class="foot">${esc(opts.footerNote)}</div>` : ''}</div></div>`;

  const pages = grupos
    .map((g) =>
      g.items
        .map((s, i) => {
          const faixa =
            i === 0
              ? `<div class="brandbar">${esc(nomeMarca(g.brand))}<span>${palavra(g.items.length, L.itemOne, L.itemMany)}</span></div>`
              : undefined;
          return renderItemPage({ ...s, labels: L }, L, faixa);
        })
        .join('\n'),
    )
    .join('\n');
  return wrapDoc(opts.title, cover + pages, autoPrint);
}

/** Baixa o relatório como arquivo .html (abre em qualquer navegador, sem depender de nada). */
export function downloadReport(filename: string, html: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.html') ? filename : `${filename}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Abre o relatório numa nova aba e dispara a impressão → a pessoa escolhe
 * "Salvar como PDF". Gera um PDF que qualquer um abre. Se o pop-up for bloqueado,
 * cai para o download do HTML (que também abre em qualquer navegador).
 */
export function printReport(html: string, fallbackName = 'relatorio') {
  const w = window.open('', '_blank');
  if (!w) {
    downloadReport(fallbackName, html);
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
